/**
 * Stats API client for hierarchical business intelligence.
 */

import { apiClient } from '@/lib/api-client'

export const statsApi = {
  /**
   * Get stats overview with hierarchical breakdown
   */
  getOverview: async (params: {
    scope: 'system' | 'tenant' | 'organization'
    tenantId?: string | null
    organizationId?: string | null
    start?: string
    end?: string
  }) => {
    const response = await apiClient.get('/v1/stats/overview', {
      params: {
        scope: params.scope,
        tenantId: params.tenantId || undefined,
        organizationId: params.organizationId || undefined,
        start: params.start,
        end: params.end
      }
    })
    return response.data
  },

  /**
   * Get weekly trends with ISO week bucketing
   */
  getWeeklyTrends: async (params: {
    weeks: number
    scope: 'system' | 'tenant' | 'organization'
    tenantId?: string | null
    organizationId?: string | null
    domain?: string
  }) => {
    const response = await apiClient.get('/v1/stats/weekly-trends', {
      params: {
        weeks: params.weeks,
        scope: params.scope,
        tenantId: params.tenantId || undefined,
        organizationId: params.organizationId || undefined,
        domain: params.domain
      }
    })
    return response.data
  },

  /**
   * Get domain-level statistics
   */
  getDomainStats: async (params: {
    page: number
    limit: number
    sort_by: string
    scope: 'system' | 'tenant' | 'organization'
    tenantId?: string | null
    organizationId?: string | null
    start?: string
    end?: string
  }) => {
    const offset = (params.page - 1) * params.limit
    const response = await apiClient.get('/v1/stats/domains', {
      params: {
        limit: params.limit,
        offset,
        sort_by: params.sort_by,
        scope: params.scope,
        tenantId: params.tenantId || undefined,
        organizationId: params.organizationId || undefined,
        start: params.start,
        end: params.end
      }
    })
    return response.data
  },

  /**
   * Get detailed view of a specific domain
   */
  getDomainDetail: async (params: {
    domain: string
    weeks?: number
    scope: 'system' | 'tenant' | 'organization'
    tenantId?: string | null
    organizationId?: string | null
    start?: string
    end?: string
  }) => {
    const response = await apiClient.get(`/v1/stats/domains/${params.domain}`, {
      params: {
        weeks: params.weeks,
        scope: params.scope,
        tenantId: params.tenantId || undefined,
        organizationId: params.organizationId || undefined,
        start: params.start,
        end: params.end
      }
    })
    return response.data
  },

  /**
   * Get all tenants with summary metrics
   */
  getTenants: async () => {
    const response = await apiClient.get('/v1/stats/tenants')
    return response.data
  },

  /**
   * Get organizations (optionally filtered by tenant)
   */
  getOrganizations: async (params: { tenantId?: string | null }) => {
    const response = await apiClient.get('/v1/stats/organizations', {
      params: {
        tenantId: params.tenantId || undefined
      }
    })
    return response.data
  }
}
